import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  DateRangeFilter,
  UniversalFilter,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import {
  Column,
  Maintenance,
  MaintenanceStatus,
  MaintenanceSeverity,
  MaintenanceReason,
  Model,
  Oem,
} from '@/types/common';
import {
  getListMaintenances,
  createMaintenance,
  updateMaintenance,
  CreateMaintenanceRequest,
  UpdateMaintenanceRequest,
} from '@/services/api/maintenances';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage } from '@/utils';
import {
  getMaintenanceSeverityOptions,
  getMaintenanceStatusOptions,
  getMaintenanceReasonOptions,
  getMaintenanceCategoryOptions,
  getCostResponsibilityOptions,
  getMaintenanceReasonLabel,
  getMaintenanceCategoryLabel,
} from '@/utils/maintenanceEnums';
import { getUserEmail } from '@/utils/storage';
import CreateMaintenanceModal from './components/CreateMaintenanceModal';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface MaintenanceRequestParams {
  page: number;
  size: number;
  search?: string;
  statuses?: string;
  severities?: string;
  oemIDs?: string;
  modelIDs?: string;
  startDate?: string;
  endDate?: string;
  reasons?: string;
  categories?: string;
  costResponsibilities?: string;
}

const MaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMaintenance, setEditingMaintenance] =
    useState<Maintenance | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });
  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);

  const currentRequestRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-2-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models',
        icon: 'ri-e-bike-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: Model) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'severities',
        title: 'Severity',
        icon: 'ri-alert-line',
        multiple: true,
        dataSource: getMaintenanceSeverityOptions(),
        labelField: 'label',
        valueField: 'value',
      },
      {
        key: 'statuses',
        title: 'Status',
        icon: 'ri-filter-3-fill',
        multiple: true,
        dataSource: getMaintenanceStatusOptions(),
        labelField: 'label',
        valueField: 'value',
      },
      {
        key: 'reasons',
        title: 'Reason',
        icon: 'ri-open-source-line',
        multiple: true,
        dataSource: getMaintenanceReasonOptions(),
        labelField: 'label',
        valueField: 'value',
      },
      {
        key: 'categories',
        title: 'Maintenance Details',
        icon: 'ri-settings-5-line',
        multiple: true,
        dataSource: getMaintenanceCategoryOptions(),
        labelField: 'label',
        valueField: 'value',
      },
      {
        key: 'costResponsibilities',
        title: 'Cost Responsibility',
        icon: 'ri-user-line',
        multiple: true,
        dataSource: getCostResponsibilityOptions(),
        labelField: 'label',
        valueField: 'value',
      },
    ],
    [models, oems]
  );

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [oemsResponse, modelsResponse] = await Promise.all([
          getListOem({ page: '1', size: '1000' }),
          getListModel({ page: '1', size: '1000' }),
        ]);
        setOems(oemsResponse.data);
        setModels(modelsResponse.data);
      } catch (error) {}
    };

    loadFilterData();
  }, []);

  const activeFiltersString = useMemo(() => {
    return JSON.stringify(activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    const fetchMaintenances = async () => {
      const currentRequestId = ++requestIdRef.current;

      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }

      const abortController = new AbortController();
      currentRequestRef.current = abortController;

      setIsLoading(true);

      try {
        const params: MaintenanceRequestParams = {
          page: currentPage,
          size: pageSize,
        };

        if (searchTerm.trim()) params.search = searchTerm.trim();

        if (dateRange.from) {
          params.startDate = dateRange.from.toISOString().split('T')[0];
        }
        if (dateRange.to) {
          params.endDate = dateRange.to.toISOString().split('T')[0];
        }

        if (activeFilters.oemIDs?.length > 0) {
          params.oemIDs = activeFilters.oemIDs.join(',');
        }
        if (activeFilters.modelIDs?.length > 0) {
          params.modelIDs = activeFilters.modelIDs.join(',');
        }
        if (activeFilters.severities?.length > 0) {
          params.severities = activeFilters.severities.join(',');
        }
        if (activeFilters.statuses?.length > 0) {
          params.statuses = activeFilters.statuses.join(',');
        }
        if (activeFilters.reasons?.length > 0) {
          params.reasons = activeFilters.reasons.join(',');
        }
        if (activeFilters.categories?.length > 0) {
          params.categories = activeFilters.categories.join(',');
        }
        if (activeFilters.costResponsibilities?.length > 0) {
          params.costResponsibilities =
            activeFilters.costResponsibilities.join(',');
        }

        const response = await getListMaintenances(params);

        if (
          !abortController.signal.aborted &&
          currentRequestId === requestIdRef.current
        ) {
          setMaintenances(response.data);
          setTotalRecords(response.pagination.total);
          currentRequestRef.current = null;
        }
      } catch (error: any) {
        if (
          !abortController.signal.aborted &&
          currentRequestId === requestIdRef.current
        ) {
          showErrorToast(
            'Error',
            extractApiErrorMessage(error, 'Failed to fetch maintenances')
          );
          currentRequestRef.current = null;
        }
      } finally {
        if (
          !abortController.signal.aborted &&
          currentRequestId === requestIdRef.current
        ) {
          setIsLoading(false);
        }
      }
    };

    fetchMaintenances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    refreshTrigger,
    searchTerm,
    dateRange.from,
    dateRange.to,
    activeFiltersString,
  ]);

  useEffect(() => {
    setExpandedRows(new Set());
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [actionDropdownOpen]);

  const toggleRowExpansion = (maintenanceId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(maintenanceId)) {
        newSet.delete(maintenanceId);
      } else {
        newSet.add(maintenanceId);
      }
      return newSet;
    });
  };

  const renderMaintenanceDetails = (
    categories: string[],
    maintenanceId: string
  ) => {
    if (!categories || categories.length === 0) {
      return <span className="text-gray-400">-</span>;
    }

    const isExpanded = expandedRows.has(maintenanceId);
    const visibleCategories = isExpanded ? categories : categories.slice(0, 2);
    const remainingCount = categories.length - 2;

    return (
      <div className="text-sm">
        {visibleCategories.map((category, index) => (
          <span
            key={index}
            className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs mr-1 mb-1"
          >
            {getMaintenanceCategoryLabel(category as any)}
          </span>
        ))}
        {remainingCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpansion(maintenanceId);
            }}
            className="inline-block border border-blue-500 text-blue-700 px-2 py-1 rounded text-xs mr-1 mb-1 transition-colors cursor-pointer"
          >
            {isExpanded ? 'Show less' : `+${remainingCount} more`}
          </button>
        )}
      </div>
    );
  };

  const columns: Column<Maintenance>[] = [
    {
      key: 'code',
      title: 'Maintenance ID',
      width: '120px',
    },
    {
      key: 'linkedIncidentCode',
      title: 'Incident ID',
      width: '120px',
    },
    {
      key: 'reason',
      title: 'Reason',
      width: '150px',
      render: (reason: MaintenanceReason) => (
        <span className="text-sm">{getMaintenanceReasonLabel(reason)}</span>
      ),
    },
    {
      key: 'vehicle',
      title: 'Vehicle',
      width: '120px',

      render: (_, record) => (
        <div className="text-sm">
          <div className="font-medium">{record.vehiclePlatNomor}</div>
          {record.vehiclePlatNomor && (
            <div className="text-gray-500">
              {record.vehicleOemName + ' - ' + record.vehicleModelName}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'categories',
      title: 'Maintenance Details',
      width: '200px',
      render: (categories: string[], record) =>
        renderMaintenanceDetails(categories, record.ID),
    },
    {
      key: 'status',
      title: 'Status',
      width: '120px',
      render: (status: MaintenanceStatus) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            status
          )}`}
        >
          {getStatusLabel(status)}
        </span>
      ),
    },
    {
      key: 'severity',
      title: 'Severity',
      width: '100px',
      sortable: true,
      render: (severity: MaintenanceSeverity) => (
        <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700">
          <span
            className={`h-2 w-2 rounded-full ${
              severity === MaintenanceSeverity.LOW
                ? 'bg-green-500'
                : severity === MaintenanceSeverity.MEDIUM
                  ? 'bg-blue-500'
                  : severity === MaintenanceSeverity.HIGH
                    ? 'bg-orange-500'
                    : severity === MaintenanceSeverity.CRITICAL
                      ? 'bg-red-500'
                      : 'bg-gray-400'
            }`}
          />
          {severity}
        </span>
      ),
    },
    {
      key: 'oemServiceCenter',
      title: 'OEM Service Center',
      width: '180px',
    },
    {
      key: 'estimatedCost',
      title: 'Estimated Cost',
      width: '120px',
      render: (cost) => `Rp. ${cost?.toLocaleString('id-ID') || '0'}`,
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      width: '120px',
      sortable: true,
      render: (date) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      key: 'expectedCompletionDate',
      title: 'Exp Completion Date',
      width: '140px',
      sortable: true,
      render: (date) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      key: 'createdBy',
      title: 'Created By',
      width: '150px',
    },
    {
      key: 'actions',
      title: '',
      width: 'w-20',
      align: 'center',
      render: (_: any, record: Maintenance, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={createMaintenanceActions(record)}
            ariaLabel={`Actions for maintenance ${record.code}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Maintenance>({
    storageKey: 'maintenance',
    columns,
  });

  const getStatusColor = (status: MaintenanceStatus): string => {
    const COLOR_MAP: Partial<Record<MaintenanceStatus, string>> = {
      [MaintenanceStatus.OPEN]: 'bg-blue-100 text-[#335CFF]',
      [MaintenanceStatus.IN_PROGRESS]: 'bg-yellow-100 text-yellow-700',
      [MaintenanceStatus.COMPLETED]: 'bg-green-100 text-[#1FC16B]',
      [MaintenanceStatus.TEMP_SWAP]: 'bg-[#FFF3EB] text-[#FA7319]',
      [MaintenanceStatus.PENDING_APPROVAL]: 'bg-[#EFEAF7] text-[#5E2AAC]',
      [MaintenanceStatus.PENDING_APPROVAL_COMPLETION]:
        'bg-[#EFEAF7] text-[#5E2AAC]',
      [MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP]:
        'bg-[#EFEAF7] text-[#5E2AAC]',
      [MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP]:
        'bg-[#EFEAF7] text-[#5E2AAC]',
      [MaintenanceStatus.PERM_SWAP]: 'bg-[#FB3748]/10 text-[#FB3748]',
      [MaintenanceStatus.CLOSED]: 'bg-gray-100 text-gray-600',
    };
    return COLOR_MAP[status] ?? 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: MaintenanceStatus): string => {
    const LABEL_MAP: Partial<Record<MaintenanceStatus, string>> = {
      [MaintenanceStatus.OPEN]: 'Open',
      [MaintenanceStatus.IN_PROGRESS]: 'In Progress',
      [MaintenanceStatus.COMPLETED]: 'Completed',
      [MaintenanceStatus.TEMP_SWAP]: 'Temporary Swap',
      [MaintenanceStatus.PENDING_APPROVAL]: 'Pending Approval',
      [MaintenanceStatus.PENDING_APPROVAL_COMPLETION]: 'Pending Approval',
      [MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP]: 'Pending Approval',
      [MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP]: 'Pending Approval',
      [MaintenanceStatus.PERM_SWAP]: 'Permanent Swap',
      [MaintenanceStatus.CLOSED]: 'Closed',
    };
    return LABEL_MAP[status] ?? status;
  };

  const handleSearch = (value: string) => {
    const searchChanged = searchTerm !== value;

    setSearchTerm(value);

    if (searchChanged) {
      setCurrentPage(1);
    } else {
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDateRangeChange = (range: {
    from: Date | null;
    to: Date | null;
  }) => {
    const dateChanged =
      dateRange.from?.getTime() !== range.from?.getTime() ||
      dateRange.to?.getTime() !== range.to?.getTime();

    const isBeingCleared = range.from === null && range.to === null;

    const hadPreviousDate = dateRange.from !== null || dateRange.to !== null;

    setDateRange(range);

    if (dateChanged) {
      if (isBeingCleared && hadPreviousDate) {
        setCurrentPage(1);

        setRefreshTrigger((prev) => prev + 1);
      } else {
        setCurrentPage(1);
      }
    } else {
    }
  };

  const handleFiltersApply = (filters: Record<string, any>) => {
    const filtersChanged =
      JSON.stringify(activeFilters) !== JSON.stringify(filters);

    setActiveFilters(filters);

    if (filtersChanged) {
      setCurrentPage(1);
    } else {
    }
  };

  const handleFiltersClear = () => {
    const hasFilters = Object.keys(activeFilters).length > 0;

    setActiveFilters({});

    if (hasFilters) {
      setCurrentPage(1);
    } else {
    }
  };

  const handleCreateMaintenance = () => {
    setEditingMaintenance(null);
    setIsModalOpen(true);
  };

  const handleEditMaintenance = (maintenance: Maintenance) => {
    setEditingMaintenance(maintenance);
    setIsModalOpen(true);
    setActionDropdownOpen(null);
  };

  // const handleMarkAsComplete = useCallback(
  //   async (maintenance: Maintenance) => {
  //     if (!maintenance.ID) return;

  //     try {
  //       const userEmail = getUserEmail();

  //       await markMaintenanceAsComplete(maintenance.ID, {
  //         updatedBy: userEmail,
  //       });

  //       showSuccessToast('Success', 'Maintenance marked as complete');
  //       setActionDropdownOpen(null);

  //       setRefreshTrigger((prev) => prev + 1);
  //     } catch (error) {
  //       const errorMessage = extractApiErrorMessage(
  //         error,
  //         'Failed to mark maintenance as complete'
  //       );
  //       showErrorToast('Error', errorMessage);
  //     }
  //   },
  //   [setRefreshTrigger]
  // );

  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const createMaintenanceActions = useCallback(
    (maintenance: Maintenance): ActionDropdownAction[] => {
      const actions: ActionDropdownAction[] = [];

      actions.push({
        key: 'view',
        label: 'Detail',
        icon: 'ri-eye-line',
        onClick: () =>
          navigate(`/maintenances/${maintenance.ID}`, {
            state: { maintenance },
          }),
        separateAfter: true,
      });

      if (maintenance.status === MaintenanceStatus.OPEN) {
        actions.push({
          key: 'edit',
          label: 'Edit',
          icon: 'ri-edit-line',
          onClick: () => handleEditMaintenance(maintenance),
          separateAfter: true,
        });
      }

      return actions;
    },
    [navigate]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingMaintenance(null);
  }, []);

  const handleMaintenanceSubmit = useCallback(
    async (data: CreateMaintenanceRequest) => {
      const userEmail = getUserEmail();

      if (editingMaintenance) {
        const updateData: UpdateMaintenanceRequest = {
          ...(data as UpdateMaintenanceRequest),
          updatedBy: userEmail,
        };

        await updateMaintenance(editingMaintenance.ID, updateData);
        showSuccessToast('Success', 'Maintenance updated successfully');
      } else {
        const createData: CreateMaintenanceRequest = {
          ...data,
          createdBy: userEmail,
        };

        await createMaintenance(createData);
        showSuccessToast('Success', 'Maintenance created successfully');
      }

      handleModalClose();
      setRefreshTrigger((prev) => prev + 1);
    },
    [editingMaintenance, handleModalClose]
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Maintenance
            </h1>
            <p className="text-[14px] text-gray-600">
              Overview of fleet maintenance and service history.
            </p>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={handleCreateMaintenance}
          >
            <i className="ri-add-line text-[12px] mr-1"></i>
            Create Maintenance
          </Button>
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              initialRange={dateRange}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>
      <TableData
        columns={visibleColumns}
        data={maintenances}
        loading={isLoading}
        onRowClick={(maintenance) =>
          navigate(`/maintenances/${maintenance.ID}`, {
            state: { maintenance },
          })
        }
      />

      <Pagination
        currentPage={currentPage}
        totalItems={totalRecords}
        itemsPerPage={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1);
        }}
      />

      <CreateMaintenanceModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleMaintenanceSubmit}
        isEdit={!!editingMaintenance}
        maintenance={editingMaintenance}
        clearFormOnClose={true}
      />
    </div>
  );
};

export default MaintenancePage;
